import { useEffect, useMemo, useState } from "react";
import {
  MDBBtn,
  MDBCol,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalHeader,
  MDBRow,
  MDBTable,
} from "mdbreact";
import { GenericProducts } from "../../../../../constants";
import { chainOf, levelsOf } from "../products/packagesEditor";

// generics usually received with a lot number and expiry
const LOT_CATEGORIES = [
  "Reagent",
  "Test Kit",
  "Control",
  "Calibrator",
  "Stain",
  "Culture Media",
  "Collection Tube",
];
const tracksLotByDefault = (pgid) =>
  LOT_CATEGORIES.includes(GenericProducts.find(pgid)?.category);

const blankVariant = () => ({
  name: "",
  unit: "",
  quantity: "",
  tests: "",
  breakdown: [],
  isDefault: false,
});

/**
 * Variant -> the simple fields of this form (pack unit, Qty / unit, tests).
 * A breakdown of 3+ levels (box > pack > case > stick) is kept as it is,
 * it is edited in Catalogs > Products.
 */
const fieldsOf = (variant, product) => {
  const levels = levelsOf(variant, product);
  const [top] = levels;
  const isTests = /^tests?$/i.test(levels[levels.length - 1]?.bu || "");
  return {
    unit: top?.bu || "",
    quantity: levels.length > 1 && !isTests ? top.qnty : "",
    tests: levels.length > 1 && isTests ? top.qnty : "",
    breakdown: levels,
  };
};

// the simple fields -> breakdown (server computes qnty)
const breakdownOf = ({ unit, quantity, tests, breakdown }, needsTests) => {
  if (breakdown?.length > 2) return breakdown.map(({ bu, su }) => ({ bu, su }));
  const pack = unit.trim();
  if (needsTests)
    return Number(tests) > 0
      ? [{ bu: pack || "kit", su: Number(tests) }, { bu: "test" }]
      : [];
  if (Number(quantity) > 1)
    return [{ bu: pack || "pack", su: Number(quantity) }, { bu: "pcs" }];
  return pack ? [{ bu: pack }] : [];
};

const blank = {
  pgid: "",
  name: "",
  manufacturer: "",
  supplier: "",
  tracksLot: false,
  description: "",
  isDefault: false,
  variants: [{ ...blankVariant(), isDefault: true }],
};

// active generics grouped by section for the picker
const groups = GenericProducts.active.reduce((result, generic) => {
  (result[generic.section] = result[generic.section] || []).push(generic);
  return result;
}, {});

// saved product -> form; older records without variants get one from their packaging/tests
const toForm = (product = {}) => {
  const variants = product.variants?.length
    ? product.variants.map((variant) => ({
        _id: variant._id,
        name: variant.name || "",
        ...fieldsOf(variant, product),
        isDefault: !!variant.isDefault,
      }))
    : [
        {
          ...blankVariant(),
          name: product._id ? "Standard" : "",
          ...fieldsOf({}, product),
          isDefault: true,
        },
      ];

  return {
    ...blank,
    pgid: product.pgid || "",
    name: product.name || "",
    manufacturer: product.manufacturer || "",
    supplier: product.supplier?._id || product.supplier || "",
    tracksLot:
      typeof product.tracksLot === "boolean"
        ? product.tracksLot
        : tracksLotByDefault(product.pgid),
    description: product.description || "",
    isDefault: !!product.isDefault,
    variants,
  };
};

/**
 * Create / update a brand (product) of a generic and its variants,
 * e.g. Syringe -> Terumo -> 3cc / 5cc, EDTA -> BD -> Tube 3mL / Micro container 0.5mL.
 * Reagents (generic uom "test") require the number of tests per unit of every variant.
 */
export default function BrandModal({
  show,
  toggle,
  selected,
  onSubmit,
  suppliers = [],
  isLoading,
}) {
  const [form, setForm] = useState(blank);
  const willCreate = !selected?._id;

  useEffect(() => {
    if (show) setForm(toForm(selected || {}));
  }, [show, selected]);

  const generic = useMemo(() => GenericProducts.find(form.pgid), [form.pgid]);
  const needsTests = generic?.uom === "test";

  const handleChange = (key, value) =>
    setForm((prev) => ({
      ...prev,
      [key]: value,
      // a new brand follows its generic: reagents, kits, tubes... track lots
      ...(key === "pgid" &&
        willCreate && { tracksLot: tracksLotByDefault(value) }),
    }));

  const handleVariant = (index, key, value) =>
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.map((variant, i) =>
        key === "isDefault"
          ? { ...variant, isDefault: i === index }
          : i === index
            ? { ...variant, [key]: value }
            : variant,
      ),
    }));

  const addVariant = () =>
    setForm((prev) => ({
      ...prev,
      variants: [...prev.variants, blankVariant()],
    }));

  const removeVariant = (index) =>
    setForm((prev) => {
      const variants = prev.variants.filter((_, i) => i !== index);
      // keep one default
      if (variants.length && !variants.some(({ isDefault }) => isDefault))
        variants[0] = { ...variants[0], isDefault: true };
      return { ...prev, variants };
    });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...(selected?._id && { _id: selected._id }),
      pgid: Number(form.pgid),
      name: form.name.trim(),
      manufacturer: form.manufacturer.trim(),
      // null clears it on update
      supplier: form.supplier || null,
      tracksLot: !!form.tracksLot,
      description: form.description,
      isDefault: form.isDefault,
      variants: form.variants.map((variant) => ({
        ...(variant._id && { _id: variant._id }),
        name: variant.name.trim(),
        breakdown: breakdownOf(variant, needsTests),
        isDefault: variant.isDefault,
      })),
    });
  };

  return (
    <MDBModal isOpen={show} toggle={toggle} backdrop size="lg">
      <MDBModalHeader
        toggle={toggle}
        className="light-blue darken-3 white-text"
      >
        <MDBIcon icon="tag" className="mr-2" />
        {willCreate ? "Add" : "Update"} Branded Product
      </MDBModalHeader>
      <MDBModalBody>
        <form onSubmit={handleSubmit}>
          <label className="small text-muted mb-1">Generic product</label>
          <select
            className="browser-default custom-select mb-1"
            value={form.pgid}
            onChange={({ target }) => handleChange("pgid", target.value)}
            required
          >
            <option value="">Select generic</option>
            {Object.entries(groups).map(([section, generics]) => (
              <optgroup key={section} label={section}>
                {generics.map(({ id, name, abbreviation }) => (
                  <option key={id} value={id}>
                    {abbreviation} – {name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {generic && (
            <small className="d-block text-muted mb-3">
              {generic.category} · {generic.section} · uom: {generic.uom} ·{" "}
              {generic.reusable
                ? "Reusable (P.O. + monthly dispense)"
                : "Per test (declared upon dispensing)"}
            </small>
          )}

          <MDBRow>
            <MDBCol md="7">
              <label className="small text-muted mb-1">Brand name</label>
              <input
                className="form-control mb-3"
                value={form.name}
                placeholder="e.g. BD Vacutainer K2EDTA, Terumo"
                onChange={({ target }) => handleChange("name", target.value)}
                required
              />
            </MDBCol>
            <MDBCol md="5">
              <label className="small text-muted mb-1">Manufacturer</label>
              <input
                className="form-control mb-3"
                value={form.manufacturer}
                placeholder="e.g. Becton Dickinson"
                onChange={({ target }) =>
                  handleChange("manufacturer", target.value)
                }
              />
            </MDBCol>
          </MDBRow>

          <label className="small text-muted mb-1">
            Preferred supplier <small>(where to order it, optional)</small>
          </label>
          <select
            className="browser-default custom-select mb-1"
            value={form.supplier}
            onChange={({ target }) => handleChange("supplier", target.value)}
          >
            <option value="">— None —</option>
            {suppliers.map(({ _id, name, displayname }) => (
              <option key={_id} value={_id}>
                {name || displayname}
              </option>
            ))}
          </select>
          <small className="d-block text-muted mb-3">
            {suppliers.length
              ? "Used as the supplier when ordering this brand."
              : "No supplier yet. Add one in Sources > Suppliers."}
          </small>

          <div className="custom-control custom-checkbox mb-3">
            <input
              type="checkbox"
              className="custom-control-input"
              id="brand-tracksLot"
              checked={!!form.tracksLot}
              onChange={({ target }) =>
                handleChange("tracksLot", target.checked)
              }
            />
            <label className="custom-control-label" htmlFor="brand-tracksLot">
              Has lot number and expiry
              <small className="text-muted d-block">
                Lot number and expiry date are required when receiving this
                brand (reagents, test kits, controls, tubes...).
              </small>
            </label>
          </div>

          <div className="custom-control custom-checkbox mb-3">
            <input
              type="checkbox"
              className="custom-control-input"
              id="brand-isDefault"
              checked={form.isDefault}
              onChange={({ target }) =>
                handleChange("isDefault", target.checked)
              }
            />
            <label className="custom-control-label" htmlFor="brand-isDefault">
              Default brand of this generic
              <small className="text-muted d-block">
                Pre-selected upon dispensing. Only one brand per generic; the
                first brand becomes default automatically.
              </small>
            </label>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-1">
            <label className="small text-muted mb-0">
              Variants{" "}
              <small>(e.g. 3cc / 5cc, Tube 3mL / Micro container 0.5mL)</small>
            </label>
            <MDBBtn
              size="sm"
              color="light"
              className="m-0"
              onClick={addVariant}
            >
              <MDBIcon icon="plus" className="mr-1" /> Variant
            </MDBBtn>
          </div>
          <MDBTable small bordered className="mb-2">
            <thead>
              <tr>
                <th
                  className="text-center"
                  style={{ width: 60 }}
                  title="Default variant"
                >
                  Default
                </th>
                <th>Variant</th>
                <th style={{ width: 110 }}>Pack unit</th>
                {!needsTests && <th style={{ width: 90 }}>Qty / unit</th>}
                {needsTests && <th style={{ width: 100 }}>Tests / unit *</th>}
                <th style={{ width: 30 }} />
              </tr>
            </thead>
            <tbody>
              {form.variants.map((variant, index) => (
                <tr key={variant._id || index}>
                  <td className="text-center align-middle">
                    {form.variants.length > 1 ? (
                      <input
                        type="radio"
                        name="variant-default"
                        checked={variant.isDefault}
                        onChange={() => handleVariant(index, "isDefault", true)}
                        title="Use this variant by default"
                      />
                    ) : (
                      // a single variant is the default by itself, nothing to choose
                      <small
                        className="text-muted"
                        title="The only variant is the default"
                      >
                        <MDBIcon icon="star" className="text-warning" /> only
                      </small>
                    )}
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={variant.name}
                      placeholder="3cc"
                      onChange={({ target }) =>
                        handleVariant(index, "name", target.value)
                      }
                      required
                    />
                  </td>
                  {variant.breakdown?.length > 2 ? (
                    <td colSpan={2} className="align-middle small">
                      {chainOf(variant.breakdown)}
                      <small className="d-block text-muted">
                        Edit in Catalogs › Products
                      </small>
                    </td>
                  ) : (
                    <>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          value={variant.unit}
                          placeholder="box"
                          onChange={({ target }) =>
                            handleVariant(index, "unit", target.value)
                          }
                        />
                      </td>
                      {!needsTests && (
                        <td>
                          <input
                            type="number"
                            min={1}
                            className="form-control form-control-sm"
                            value={variant.quantity}
                            placeholder="100"
                            onChange={({ target }) =>
                              handleVariant(index, "quantity", target.value)
                            }
                          />
                        </td>
                      )}
                    </>
                  )}
                  {needsTests && (
                    <td>
                      <input
                        type="number"
                        min={1}
                        className="form-control form-control-sm"
                        value={variant.tests}
                        placeholder="100"
                        onChange={({ target }) =>
                          handleVariant(index, "tests", target.value)
                        }
                        required
                      />
                    </td>
                  )}
                  <td className="align-middle text-center">
                    {form.variants.length > 1 && (
                      <MDBIcon
                        icon="trash"
                        className="text-danger"
                        style={{ cursor: "pointer" }}
                        onClick={() => removeVariant(index)}
                        title="Remove variant"
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </MDBTable>
          {form.variants.length > 1 && (
            <small className="d-block text-muted mb-2">
              <MDBIcon icon="info-circle" className="mr-1" />
              The first variant is the default until you pick another; the
              default is pre-selected when tagging and dispensing.
            </small>
          )}
          {needsTests && (
            <small className="d-block text-muted mb-3">
              Reagent: 1 unit (e.g. kit) holds the tests per unit; each test
              uses 1.
            </small>
          )}

          <label className="small text-muted mb-1">Description</label>
          <textarea
            className="form-control mb-3"
            rows={2}
            value={form.description}
            onChange={({ target }) => handleChange("description", target.value)}
          />

          <div className="text-center">
            <MDBBtn type="submit" color="info" rounded disabled={isLoading}>
              {willCreate ? "Submit" : "Update"}
            </MDBBtn>
          </div>
        </form>
      </MDBModalBody>
    </MDBModal>
  );
}
